// Pure helpers around the LiveKit host — the two cheap hardenings that came
// out of the production call investigation (MEET-FEATURES-BUILD-1 item 4;
// origin: the MEET-LIVEKIT-PROJECT-MISMATCH-1 re-diagnosis, tickets/archive/
// 2026-08/opus.md). No DOM, no SDK: callable under node, wired at the call
// sites (connectCall.js / pages/guest.js) the same way pages/index.js wires
// `_resolveMeetSeed`.
//
// (a) resolveLivekitPair — a LiveKit token is only valid for the host it was
//     signed with, so `{ token, url }` must come from ONE response shape,
//     atomically. The server speaks two: the canonical guest shape
//     `{ token, url, identity }` and the member mint `{ livekitToken,
//     livekitUrl }`. The old guest read (`tokenInfo.url || tokenInfo.
//     livekitUrl` next to `tokenInfo.token || tokenInfo.livekitToken`) could
//     cross-pair the halves, or hand `connect()` an undefined url and let the
//     SDK surface it as a generic websocket failure. Now: pair or refuse,
//     with a reason that names the problem.
//
// (b) the prewarm host cache — connectCall prewarms DNS/TLS against the
//     last-known host while the mint round-trips. The cache was one unkeyed
//     localStorage entry shared by every API environment reachable from the
//     same origin, written unconditionally and never compared. Now keyed by
//     API base, compared on every mint (the move is reported so the call
//     site can log it — the exact diagnostic the investigation lacked), and
//     cleared when a mint does not confirm a host.

export const LEGACY_PREWARM_KEY = 'meet:livekitUrl'

const _isHostUrl = (u) => typeof u === 'string' && /^(wss?|https?):\/\/[^\s/]+/i.test(u)

export const resolveLivekitPair = (tokenInfo) => {
  if (!tokenInfo || typeof tokenInfo !== 'object') {
    throw new Error('livekit token/url pair missing: empty token response')
  }
  const canonical = tokenInfo.token && tokenInfo.url ? { token: tokenInfo.token, url: tokenInfo.url } : null
  const legacy =
    tokenInfo.livekitToken && tokenInfo.livekitUrl
      ? { token: tokenInfo.livekitToken, url: tokenInfo.livekitUrl }
      : null
  if (!canonical && !legacy) {
    const seen = ['token', 'url', 'livekitToken', 'livekitUrl'].filter((k) => tokenInfo[k]).join(',') || 'none'
    throw new Error(`livekit token/url pair missing: incomplete token response (fields: ${seen})`)
  }
  if (canonical && legacy && canonical.url !== legacy.url) {
    throw new Error('livekit token response shapes disagree on the host url — refusing to dial')
  }
  const pair = canonical || legacy
  if (!_isHostUrl(pair.url)) {
    throw new Error(`livekit url is not a ws(s)/http(s) host: ${String(pair.url).slice(0, 40)}`)
  }
  return { token: pair.token, url: pair.url, shape: canonical ? 'canonical' : 'legacy' }
}

const _normBase = (apiBase) => String(apiBase || '').trim().replace(/\/+$/, '') || 'default'

export const prewarmHostKey = (apiBase) => `${LEGACY_PREWARM_KEY}:${_normBase(apiBase)}`

export const readPrewarmHost = (storage, apiBase) => {
  try {
    const v = storage?.getItem?.(prewarmHostKey(apiBase))
    return _isHostUrl(v) ? v : null
  } catch (_) {
    return null
  }
}

// Returns { changed, previous, url } — `changed` is true when a PREVIOUSLY
// cached host moved (or was cleared); a cold cache is not a move. `previous`
// is the host the cache held before this mint.
export const rememberPrewarmHost = (storage, apiBase, freshUrl) => {
  const previous = readPrewarmHost(storage, apiBase)
  const url = _isHostUrl(freshUrl) ? freshUrl : null
  const key = prewarmHostKey(apiBase)
  try {
    // The unkeyed legacy entry predates environment scoping — drop it so a
    // host cached under another API base can never be read back as ours.
    storage?.removeItem?.(LEGACY_PREWARM_KEY)
    if (url) storage?.setItem?.(key, url)
    else storage?.removeItem?.(key)
  } catch (_) {
    /* prewarm cache only — never block a join on storage */
  }
  return { changed: previous !== null && previous !== url, previous, url }
}
